// SQLite recreate-path planner (v2 Slice H3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice H3 (lifts v1 R1).
//
// SQLite engines < 3.35.0 don't support DROP COLUMN, and no SQLite version
// supports ALTER COLUMN at all (type / nullability / default change). The
// idiomatic workaround is the "12-step" CREATE-COPY-RENAME-DROP recreate
// procedure: build a fresh table with the post-edit shape, copy data over,
// drop the original, rename the new table to the original name. Wrapped in
// `BEGIN`..`COMMIT` so a partial failure rolls back.
//
// The applicator emits the multi-statement sequence as a single appended
// PlannedByteEdit; downstream `emitSource` splices it after the source
// CREATE TABLE statement. Foreign-key disable (`PRAGMA foreign_keys = OFF`)
// wrapping is documented as the consumer's responsibility — the applicator
// stays inside the byte-edit boundary and does not emit pragma statements.
//
// Dependent indexes/triggers are NOT recreated. `DROP TABLE` (above) also
// drops any index or trigger attached to the original table, and the
// synthesized sequence never re-issues `CREATE INDEX` / `CREATE TRIGGER` to
// restore them afterwards — re-creating those objects post-recreate is the
// consumer's responsibility, same as the FK-pragma wrapping above.
//
// Source structure must be `create-table`: the recreate path needs the full
// post-edit column shape, which only the CREATE TABLE source surfaces. For
// an ALTER TABLE source the applicator throws with a documented hint.
//
// Table-level constraints the extractor could not fold into a plain column
// (composite PRIMARY KEY, table-level FOREIGN KEY / CHECK / UNIQUE, etc.) are
// parked in the source structure's `opaqueRanges` and are invisible to
// `synthesizeRecreate`, which only knows `s.columns`. Recreating such a table
// would silently drop those constraints, so `planSqliteRecreate` refuses
// (throws) whenever `opaqueRanges` is non-empty rather than emitting a
// narrower table.

import { rangeFromOffsets } from '../../../../sql-grammar/document/lineIndex';
import type { IdentifierQuoting, ParseOptions, QualifiedName } from '../../../../sql-grammar/model';
import type { ColumnChange, ColumnDef, DdlEdit, DdlStructure, PlannedByteEdit } from '../../../types';

import { findKeywordAtTopLevel, skipWsCommentsLocal } from '../../extract/shared';
import { readIdentifier } from '../../../scope/identifiers';
import { pickAppendInsertion } from '../appendInsertion';
import type { DdlEditCtx } from '../applyDdlEdit';
import { formatColumnBody, tableText } from '../dialect';
import { ENGINE_DEFAULT_QUOTING, inferQuoting, quoteWithStyle } from '../dialect.quoting';

type CreateTableStruct = Extract<DdlStructure, { kind: 'create-table' }>;
type SupportedEdit = Extract<
  DdlEdit,
  { op: 'add-column' | 'drop-column' | 'rename-column' | 'alter-column' }
>;

const SUPPORTED_OPS: ReadonlySet<DdlEdit['op']> = new Set([
  'add-column', 'drop-column', 'rename-column', 'alter-column',
]);

export function isSqliteRecreateEligibleOp(op: DdlEdit['op']): boolean {
  return SUPPORTED_OPS.has(op);
}

export function planSqliteRecreate(
  ctx: DdlEditCtx,
  edit: SupportedEdit,
): readonly PlannedByteEdit[] {
  if (ctx.structure.kind !== 'create-table') {
    throw new Error(
      `applyDdlEdit: sqliteRecreate requires a 'create-table' source structure `
        + `(got '${ctx.structure.kind}'). The recreate path needs the full column `
        + `shape — pass the source CREATE TABLE statement, not an ALTER TABLE.`,
    );
  }
  if (ctx.structure.columns.length === 0) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate requires at least one extracted column `
        + `(empty CREATE TABLE column list — e.g. CREATE TABLE … AS SELECT).`,
    );
  }
  if (ctx.structure.opaqueRanges.length > 0) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate cannot rebuild this table — the source `
        + `CREATE TABLE has ${ctx.structure.opaqueRanges.length} table-level `
        + `clause(s) the extractor could not fold into a plain column (e.g. a `
        + `composite PRIMARY KEY, a table-level FOREIGN KEY, or a table-level `
        + `CHECK/UNIQUE constraint). The recreate path only knows the columns `
        + `it extracted, so rebuilding from them would silently drop those `
        + `constraints. Rebuild the table by hand so the new CREATE TABLE `
        + `also restates them.`,
    );
  }
  const s = ctx.structure;
  const synth = synthesizeRecreate(ctx, s, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source, ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:sqlite-recreate`,
  }];
}

function synthesizeRecreate(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  edit: SupportedEdit,
): string {
  const oldText = tableText(ctx.engine, s.table);
  const newText = makeInterimTableText(ctx, s.table);
  // SQLite forbids a schema-qualified identifier as the `RENAME TO` target —
  // the new name must be a bare (optionally quoted) leaf; the schema is
  // implied by the (qualified) table being altered. So the rename target uses
  // the bare, source-consistent leaf — never `oldText` (which may be qualified).
  const renameTarget = quotedLeaf(ctx, s.table, s.table.name);
  const view = buildPostEditView(ctx, s, edit);
  const stmts: string[] = [
    'BEGIN',
    `CREATE TABLE ${newText} (${view.columnSegments.join(', ')})`,
    `INSERT INTO ${newText} (${view.insertCols.join(', ')}) `
      + `SELECT ${view.selectCols.join(', ')} FROM ${oldText}`,
    `DROP TABLE ${oldText}`,
    `ALTER TABLE ${newText} RENAME TO ${renameTarget}`,
    'COMMIT',
  ];
  return stmts.join(';\n');
}

interface PostEditView {
  /** Post-edit column body strings for the new CREATE TABLE column list. */
  readonly columnSegments: readonly string[];
  /** Column-name list for `INSERT INTO new (cols)`. */
  readonly insertCols: readonly string[];
  /** Column-name list for `SELECT cols FROM old`. */
  readonly selectCols: readonly string[];
}

function buildPostEditView(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  edit: SupportedEdit,
): PostEditView {
  if (edit.op === 'drop-column') return viewForDrop(ctx, s, edit.columnName);
  if (edit.op === 'rename-column') return viewForRename(ctx, s, edit.oldName, edit.newName);
  if (edit.op === 'alter-column') return viewForAlter(ctx, s, edit);
  return viewForAdd(ctx, s, edit);
}

function viewForDrop(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  dropName: string,
): PostEditView {
  const idx = findColumn(s.columns, dropName, 'drop-column');
  const remaining = s.columns.filter((_, i) => i !== idx);
  if (remaining.length === 0) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate cannot drop the only column '${dropName}' `
        + `(would produce an invalid empty column list)`,
    );
  }
  return {
    columnSegments: remaining.map((c) => sliceColumnText(ctx, c)),
    insertCols: remaining.map((c) => quotedColName(ctx, c)),
    selectCols: remaining.map((c) => quotedColName(ctx, c)),
  };
}

function viewForRename(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  oldName: string,
  newName: string,
): PostEditView {
  const idx = findColumn(s.columns, oldName, 'rename-column');
  return {
    columnSegments: s.columns.map((c, i) => {
      if (i === idx) return rebuildColumnWithName(ctx, c, newName);
      return sliceColumnText(ctx, c);
    }),
    insertCols: s.columns.map((c, i) =>
      i === idx
        ? emitColName(newName, detectColumnQuoting(ctx, c))
        : quotedColName(ctx, c)),
    selectCols: s.columns.map((c) => quotedColName(ctx, c)),
  };
}

function viewForAlter(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  edit: Extract<SupportedEdit, { op: 'alter-column' }>,
): PostEditView {
  const idx = findColumn(s.columns, edit.columnName, 'alter-column');
  return {
    columnSegments: s.columns.map((c, i) => {
      // F17: rebuild the altered column from its ORIGINAL source text, splicing
      // in only the changed attribute. Re-emitting from the lossy `ColumnDef`
      // via `formatColumnBody` dropped every inline constraint/attribute the
      // source carried (PRIMARY KEY, AUTOINCREMENT, UNIQUE, CHECK, COLLATE, and
      // any DEFAULT/nullable the change does not touch) — data-loss on a path
      // that exists specifically to PRESERVE those semantics.
      if (i === idx) return rebuildAlteredColumnFromSource(ctx, c, edit.change);
      return sliceColumnText(ctx, c);
    }),
    insertCols: s.columns.map((c) => quotedColName(ctx, c)),
    selectCols: s.columns.map((c) => quotedColName(ctx, c)),
  };
}

/** Rebuild the altered column body by splicing the change into the column's
 *  ORIGINAL source text (F17). The column text is `name <type> <tail>`, where
 *  `<tail>` carries every inline attribute (PRIMARY KEY, AUTOINCREMENT, UNIQUE,
 *  CHECK, COLLATE, DEFAULT, NULL/NOT NULL). We:
 *    1. locate the name token and the type span exactly the way the extractor
 *       does (`findKeywordAtTopLevel` over `TYPE_TERMINATORS`),
 *    2. swap in the new type when `change.type` is set (keep the original
 *       spelling/whitespace otherwise),
 *    3. for nullable / default changes, remove ONLY that clause from the tail
 *       (top-level-aware via `findKeywordAtTopLevel`, so a `CHECK (... NOT NULL)`
 *       or a parenthesized `DEFAULT` expression is untouched) and re-append the
 *       requested form — preserving every other inline attribute verbatim.
 *  This guarantees no inline attribute is lost regardless of what the (lossy)
 *  `ColumnDef` extractor captured. Falls back to `formatColumnBody` only when
 *  the source range or the name token cannot be located — the pre-F17 behavior,
 *  used for synthetic columns without a faithful source span. */
function rebuildAlteredColumnFromSource(
  ctx: DdlEditCtx,
  col: ColumnDef,
  change: ColumnChange,
): string {
  if (!col.range) return formatColumnBody(ctx.engine, mergeColumnChange(col, change), false);
  const { source, options } = ctx;
  const start = col.range.startOffset;
  const end = col.range.endOffset;

  const nameTok = readIdentifier(source, start, end, options);
  if (!nameTok) return formatColumnBody(ctx.engine, mergeColumnChange(col, change), false);
  const typeStart = skipWsCommentsLocal(source, nameTok.end, end, options);
  const typeHit = findKeywordAtTopLevel(source, options, typeStart, end, TYPE_TERMINATORS);
  const typeEnd = typeHit ? typeHit.start : end;

  const head = source.slice(start, typeStart); // name + whitespace up to the type
  const newType = change.type ?? source.slice(typeStart, typeEnd).trimEnd();

  // Collect tail-clause spans to remove (only those the change actually
  // touches), then strip them from the original tail in a single descending
  // pass so offsets never drift. Re-append the requested nullability/default.
  const removals: { readonly from: number; readonly to: number }[] = [];
  if (change.nullable !== undefined) {
    const c = findNullabilityClause(source, options, typeEnd, end);
    if (c) removals.push(c);
  }
  if (change.default !== undefined) {
    const c = findDefaultClause(source, options, typeEnd, end);
    if (c) removals.push(c);
  }

  let tail = source.slice(typeEnd, end);
  removals.sort((a, b) => b.from - a.from);
  for (const r of removals) {
    tail = `${tail.slice(0, r.from - typeEnd)}${tail.slice(r.to - typeEnd)}`;
  }
  tail = collapseTailWhitespace(tail);

  if (change.nullable !== undefined) tail = appendClause(tail, change.nullable ? 'NULL' : 'NOT NULL');
  if (change.default !== undefined && change.default.kind === 'set') {
    tail = appendClause(tail, `DEFAULT ${change.default.expr}`);
  }
  return `${head}${newType}${tail}`;
}

/** Normalize a tail after clause removal: collapse internal whitespace runs to
 *  a single space and trim trailing whitespace. Keeps a single leading space so
 *  the tail re-joins the type cleanly when non-empty. */
function collapseTailWhitespace(tail: string): string {
  const collapsed = tail.replace(/\s+/g, ' ').replace(/\s+$/, '');
  if (collapsed === '' || collapsed === ' ') return '';
  return collapsed.startsWith(' ') ? collapsed : ` ${collapsed}`;
}

/** Append a clause (e.g. `NOT NULL`, `DEFAULT 0`) to a (possibly empty) tail,
 *  guaranteeing exactly one separating space. */
function appendClause(tail: string, clause: string): string {
  return tail === '' ? ` ${clause}` : `${tail} ${clause}`;
}

/** Locate the top-level `NULL` / `NOT NULL` clause inside the tail span
 *  `[tailStart, end)` (offsets into `source`). Returns the `[from, to)` source
 *  offsets of that clause, or `null` when none is declared. */
function findNullabilityClause(
  source: string,
  options: ParseOptions,
  tailStart: number,
  end: number,
): { readonly from: number; readonly to: number } | null {
  const notHit = findKeywordAtTopLevel(source, options, tailStart, end, new Set(['NOT']));
  if (notHit) {
    const next = skipWsCommentsLocal(source, notHit.end, end, options);
    const after = readIdentifier(source, next, end, options);
    if (after && !after.quoted && after.value.toUpperCase() === 'NULL') {
      return { from: notHit.start, to: after.end };
    }
  }
  const nullHit = findKeywordAtTopLevel(source, options, tailStart, end, new Set(['NULL']));
  if (nullHit) return { from: nullHit.start, to: nullHit.end };
  return null;
}

/** Locate the top-level `DEFAULT <expr>` clause inside the tail span. The
 *  expression runs from the `DEFAULT` keyword up to the next top-level inline
 *  attribute keyword (or the end of the tail). Returns the `[from, to)` source
 *  offsets of the whole clause, or `null` when no default is declared. */
function findDefaultClause(
  source: string,
  options: ParseOptions,
  tailStart: number,
  end: number,
): { readonly from: number; readonly to: number } | null {
  const hit = findKeywordAtTopLevel(source, options, tailStart, end, new Set(['DEFAULT']));
  if (!hit) return null;
  const afterKeyword = findKeywordAtTopLevel(source, options, hit.end, end, TYPE_TERMINATORS_AFTER_DEFAULT);
  let to = afterKeyword ? afterKeyword.start : end;
  while (to > hit.end && /\s/.test(source[to - 1])) to--; // trim trailing whitespace
  return { from: hit.start, to };
}

/** Keywords that terminate the declared-type run in a simple column. Mirrors
 *  the CREATE TABLE extractor's `TYPE_TERMINATORS`
 *  (ddl/extract/createTable.column.ts): the F17 type-span splice must cut the
 *  type at exactly the offset the extractor did, so this set MUST stay in sync
 *  with it. Kept local (rather than imported) to avoid touching the vendored
 *  extractor file — keep the two lists identical if either changes. */
const TYPE_TERMINATORS: ReadonlySet<string> = new Set([
  'NULL', 'NOT', 'DEFAULT', 'IDENTITY', 'AUTO_INCREMENT', 'AUTOINCREMENT',
  'PRIMARY', 'UNIQUE', 'COLLATE', 'COMMENT', 'CONSTRAINT',
  'INDEX', 'KEY', 'CHECK', 'VISIBLE', 'INVISIBLE',
  'STORED', 'VIRTUAL', 'PERSISTED', 'ROWGUIDCOL', 'SPARSE',
  'ENABLE', 'DISABLE', 'ENCRYPTED', 'MASKED', 'COMPRESSED',
  'ON', 'CHARACTER',
]);

/** Inline-attribute keywords that terminate a `DEFAULT <expr>` run at top
 *  level. A default expression ends at the next column constraint keyword.
 *  Reuses the extractor's terminator vocabulary minus `DEFAULT` itself. */
const TYPE_TERMINATORS_AFTER_DEFAULT: ReadonlySet<string> = new Set([
  'NULL', 'NOT', 'PRIMARY', 'UNIQUE', 'COLLATE', 'CHECK', 'REFERENCES',
  'CONSTRAINT', 'GENERATED', 'AS', 'AUTOINCREMENT', 'KEY',
]);

function viewForAdd(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  edit: Extract<SupportedEdit, { op: 'add-column' }>,
): PostEditView {
  const newCol = edit.column;
  if (s.columns.some((c) => c.name === newCol.name)) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate add-column '${newCol.name}' collides with `
        + `an existing column of the same name`,
    );
  }
  const existing = s.columns.map((c) => sliceColumnText(ctx, c));
  const newBody = formatColumnBody(ctx.engine, newCol, s.table.quoted, s.table);
  return {
    columnSegments: [...existing, newBody],
    // Skip the new column in the INSERT/SELECT lists — the new column takes
    // its declared DEFAULT (or NULL when nullable). Consumers that need to
    // backfill a non-null value should follow the recreate path with their
    // own UPDATE statement.
    insertCols: s.columns.map((c) => quotedColName(ctx, c)),
    selectCols: s.columns.map((c) => quotedColName(ctx, c)),
  };
}

function findColumn(cols: readonly ColumnDef[], name: string, opLabel: string): number {
  const idx = cols.findIndex((c) => c.name === name);
  if (idx < 0) {
    throw new Error(`applyDdlEdit: sqliteRecreate ${opLabel} '${name}' not found in column list`);
  }
  return idx;
}

function sliceColumnText(ctx: DdlEditCtx, col: ColumnDef): string {
  if (!col.range) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate cannot slice column '${col.name}' without a source range`,
    );
  }
  return ctx.source.slice(col.range.startOffset, col.range.endOffset);
}

const COLUMN_HEAD_RE = /^(\[[^\]]+\]|"[^"]+"|`[^`]+`|[A-Za-z_][A-Za-z0-9_$]*)/;

/** Detect the quoting style the source used for a column's leading name token.
 *  Names can be quoted (`[c]`, `"c"`, `` `c` ``) or unquoted; all four shapes
 *  start at offset 0 of the column segment because `column.range` was trimmed
 *  by the extractor. `ColumnDef` carries no quoting field, so the source slice
 *  is the only quoting source of truth. Returns `'unquoted'` on no-match. */
function detectColumnQuoting(ctx: DdlEditCtx, col: ColumnDef): IdentifierQuoting {
  const head = COLUMN_HEAD_RE.exec(sliceColumnText(ctx, col))?.[0];
  if (!head) return 'unquoted';
  return head.startsWith('[') ? 'bracket'
    : head.startsWith('"') ? 'double-quote'
    : head.startsWith('`') ? 'backtick'
    : 'unquoted';
}

// SQLite reserved-word set (per sqlite.org "Keywords"). SQLite's tokenizer
// accepts many of these BARE in CREATE TABLE column position (it's lenient
// there), but the SAME bare word is a syntax error inside an INSERT-column-
// list / SELECT-list — e.g. `CREATE TABLE foo (cast TEXT)` is valid yet
// `INSERT INTO foo (cast) SELECT cast FROM …` is not. SED-4 reused only the
// source's quoting style, so such a name leaked through bare and produced
// invalid recreate SQL; NEW-2 force-quotes it. There is no shared SQLite
// reserved predicate in the codebase, so this minimal local set is the
// source of truth for the recreate path only.
const SQLITE_RESERVED_WORDS: ReadonlySet<string> = new Set([
  'ABORT', 'ACTION', 'ADD', 'AFTER', 'ALL', 'ALTER', 'ALWAYS', 'ANALYZE',
  'AND', 'AS', 'ASC', 'ATTACH', 'AUTOINCREMENT', 'BEFORE', 'BEGIN',
  'BETWEEN', 'BY', 'CASCADE', 'CASE', 'CAST', 'CHECK', 'COLLATE',
  'COLUMN', 'COMMIT', 'CONFLICT', 'CONSTRAINT', 'CREATE', 'CROSS',
  'CURRENT', 'CURRENT_DATE', 'CURRENT_TIME', 'CURRENT_TIMESTAMP',
  'DATABASE', 'DEFAULT', 'DEFERRABLE', 'DEFERRED', 'DELETE', 'DESC',
  'DETACH', 'DISTINCT', 'DO', 'DROP', 'EACH', 'ELSE', 'END', 'ESCAPE',
  'EXCEPT', 'EXCLUDE', 'EXCLUSIVE', 'EXISTS', 'EXPLAIN', 'FAIL', 'FILTER',
  'FIRST', 'FOLLOWING', 'FOR', 'FOREIGN', 'FROM', 'FULL', 'GENERATED',
  'GLOB', 'GROUP', 'GROUPS', 'HAVING', 'IF', 'IGNORE', 'IMMEDIATE', 'IN',
  'INDEX', 'INDEXED', 'INITIALLY', 'INNER', 'INSERT', 'INSTEAD',
  'INTERSECT', 'INTO', 'IS', 'ISNULL', 'JOIN', 'KEY', 'LAST', 'LEFT',
  'LIKE', 'LIMIT', 'MATCH', 'MATERIALIZED', 'NATURAL', 'NO', 'NOT',
  'NOTHING', 'NOTNULL', 'NULL', 'NULLS', 'OF', 'OFFSET', 'ON', 'OR',
  'ORDER', 'OTHERS', 'OUTER', 'OVER', 'PARTITION', 'PLAN', 'PRAGMA',
  'PRECEDING', 'PRIMARY', 'QUERY', 'RAISE', 'RANGE', 'RECURSIVE',
  'REFERENCES', 'REGEXP', 'REINDEX', 'RELEASE', 'RENAME', 'REPLACE',
  'RESTRICT', 'RETURNING', 'RIGHT', 'ROLLBACK', 'ROW', 'ROWS', 'SAVEPOINT',
  'SELECT', 'SET', 'TABLE', 'TEMP', 'TEMPORARY', 'THEN', 'TIES', 'TO',
  'TRANSACTION', 'TRIGGER', 'UNBOUNDED', 'UNION', 'UNIQUE', 'UPDATE',
  'USING', 'VACUUM', 'VALUES', 'VIEW', 'VIRTUAL', 'WHEN', 'WHERE',
  'WINDOW', 'WITH', 'WITHOUT',
]);

// Identifier-shape predicates mirror `scope/identifiers.ts` (IDENT_START /
// IDENT_CONT). A name is safe to emit BARE only when it has plain identifier
// shape AND is not a SQLite reserved word.
const SQLITE_IDENT_START = /[A-Za-z_]/;
const SQLITE_IDENT_CONT = /[A-Za-z0-9_$]/;

/** A name is unsafe as a BARE identifier in an INSERT/SELECT column list when
 *  it does not have plain identifier shape OR collides with a SQLite reserved
 *  word. SQLite tolerates bare reserved words in CREATE TABLE column position
 *  but not in INSERT/SELECT, so the recreate path must force-quote them. */
function isUnsafeBareSqliteName(name: string): boolean {
  if (name.length === 0) return true;
  if (!SQLITE_IDENT_START.test(name[0])) return true;
  for (let k = 1; k < name.length; k++) {
    if (!SQLITE_IDENT_CONT.test(name[k])) return true;
  }
  return SQLITE_RESERVED_WORDS.has(name.toUpperCase());
}

/** Emit `name` for an INSERT/SELECT column slot, given the column's detected
 *  source quoting style. SED-4 preserves the source style for quoted names.
 *  NEW-2: when the source wrote the name BARE but it is unsafe-bare (reserved
 *  word / non-identifier shape), force the engine-default SQLite quoting style
 *  so the recreate INSERT/SELECT stays valid SQLite. Plain safe identifiers
 *  written bare stay bare (SED-4 regression guard). */
function emitColName(name: string, sourceQuoting: IdentifierQuoting): string {
  if (sourceQuoting === 'unquoted' && isUnsafeBareSqliteName(name)) {
    return quoteWithStyle(name, ENGINE_DEFAULT_QUOTING.sqlite);
  }
  return quoteWithStyle(name, sourceQuoting);
}

/** Re-emit `col.name` for INSERT/SELECT column lists using the source's
 *  detected quoting style so reserved/special names round-trip as valid SQL.
 *  SED-4: reuses the column's *source* quoting style for quoted names.
 *  NEW-2: a bare-but-reserved source name (unquoted in the CREATE TABLE but a
 *  SQLite reserved word) is now force-quoted with the engine-default SQLite
 *  style via `emitColName` — SQLite accepts it bare in the CREATE TABLE column
 *  list yet rejects it bare in the recreate INSERT/SELECT column list. */
function quotedColName(ctx: DdlEditCtx, col: ColumnDef): string {
  return emitColName(col.name, detectColumnQuoting(ctx, col));
}

function rebuildColumnWithName(
  ctx: DdlEditCtx,
  col: ColumnDef,
  newName: string,
): string {
  const text = sliceColumnText(ctx, col);
  // Replace the leading column name. The throw-on-no-match contract here is
  // load-bearing (callers rely on it), so keep an explicit guard rather than
  // leaning on `detectColumnQuoting`'s `'unquoted'` no-match fallback.
  const match = COLUMN_HEAD_RE.exec(text);
  if (!match) {
    throw new Error(
      `applyDdlEdit: sqliteRecreate could not locate the leading name token in `
        + `column segment ${JSON.stringify(text)}`,
    );
  }
  const head = match[0];
  const quoting = detectColumnQuoting(ctx, col);
  const replacement = quoting === 'unquoted' ? newName : quoteWithStyle(newName, quoting);
  return replacement + text.slice(head.length);
}

function mergeColumnChange(
  col: ColumnDef,
  change: { type?: string; nullable?: boolean; default?: { kind: 'set'; expr: string } | { kind: 'drop' } },
): ColumnDef {
  return {
    ...col,
    typeRef: change.type ?? col.typeRef,
    nullable: change.nullable !== undefined ? change.nullable : col.nullable,
    defaultText: change.default === undefined
      ? col.defaultText
      : change.default.kind === 'drop'
        ? undefined
        : change.default.expr,
  };
}

/** Quote a single (schema-less) table-name leaf the way SQLite recreate needs:
 *  preserve the source's quoting style; emit bare only when the source was
 *  bare. Never consults `table.raw` (which may be schema-qualified) — rebuilds
 *  from `leafName` + the source's inferred quoting. Shared by the interim
 *  table name and the (mandatorily bare) RENAME-TO target so the two cannot
 *  diverge. */
function quotedLeaf(ctx: DdlEditCtx, table: QualifiedName, leafName: string): string {
  const quoting = inferQuoting(table, ctx.engine);
  return quoting === 'unquoted' && !table.quoted
    ? leafName
    : quoteWithStyle(leafName, quoting === 'unquoted' ? 'double-quote' : quoting);
}

function makeInterimTableText(ctx: DdlEditCtx, table: QualifiedName): string {
  const leaf = quotedLeaf(ctx, table, `${table.name}__new`);
  if (table.schema) {
    const schemaQuoting = inferQuoting(table, ctx.engine);
    const schema = schemaQuoting === 'unquoted' && !table.quoted
      ? table.schema
      : quoteWithStyle(table.schema, schemaQuoting === 'unquoted' ? 'double-quote' : schemaQuoting);
    return `${schema}.${leaf}`;
  }
  return leaf;
}
